import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTopicDetails } from '../services/api';
import styled from 'styled-components';
import { Container, Box, Stack, Typography, Chip } from '@mui/material';
import { AudioTechniquesBlock, AIChatBlock, CourseBlock } from '../components/blocks';
import FeedbackBlock from '../components/blocks/FeedbackBlock';

interface Topic {
  id: number;
  icon: string;
  title: string;
  description: string;
  category: string;
  slug: string;
  html_content: string;
  audio_techniques: {
    id: number;
    title: string;
    duration: string;
    type: string;
  }[];
  related_courses: {
    id: number;
    title: string;
    description: string;
    type: string;
    duration_formatted: string;
    lessons_count: number;
  }[];
  tags?: string[];
}

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: #666;
`;

const Content = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    margin: 2rem 0 1rem;
  }
  
  p, ul {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
`;

const Feedback = styled.div`
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
  
  h3 {
    margin-bottom: 1rem;
  }
`;

const FeedbackForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  textarea {
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-height: 100px;
  }
  
  button {
    background: #28a745;
    color: white;
    border: none;
    padding: 1rem;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
      background: #218838;
    }
  }
`;

const Loading = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
  color: #666;
`;

const Error = styled.div`
  text-align: center;
  padding: 2rem;
  color: #dc3545;
  font-size: 1.2rem;
`;

const TopicDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState('');
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        if (!slug) {
          setError('Slug не указан');
          return;
        }
        const data = await getTopicDetails(slug);
        setTopic(data);
      } catch (err) {
        let errorMessage = 'Произошла ошибка при загрузке темы';
        if (typeof err === 'object' && err && 'message' in err && typeof (err as any).message === 'string') {
          errorMessage = (err as any).message;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchTopic();
  }, [slug]);

  const handleFeedbackSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement feedback submission
    console.log('Feedback submitted:', feedback);
    setFeedback('');
  };

  if (loading) {
    return <Loading>Загрузка...</Loading>;
  }

  if (error) {
    return <Error>{error}</Error>;
  }

  if (!topic) {
    return <Error>Тема не найдена</Error>;
  }

  return (
    <Container>
      <Header>
        <Title>{topic.title}</Title>
        <Description>{topic.description}</Description>
      </Header>
      
      <Content dangerouslySetInnerHTML={{ __html: topic.html_content }} />
      
      {/* AI Chat Section */}
      <AIChatBlock 
        title="Нужна помощь психолога?"
        description="Наш AI-ассистент готов помочь вам разобраться в теме более детально"
        buttonText="Начать диалог"
        onStartChat={() => navigate('/chat')}
      />
      
      {/* Audio Techniques Section */}
      <AudioTechniquesBlock 
        techniques={topic.audio_techniques}
        onStartTechnique={(technique) => {
          // Handle starting a technique
          console.log('Starting technique:', technique);
        }}
      />

      {/* Course Section */}
      <CourseBlock 
        title="Курсы"
        description={`Пройдите наши курсы, чтобы глубже разобраться в теме "${topic.title}"`}
        resources={topic.related_courses.map(course => ({
          title: course.title,
          description: `${course.type} • ${course.duration_formatted} • ${course.lessons_count} уроков`,
          type: course.type
        }))}
        onStartCourse={() => navigate('/courses')}
      />

      {/* Tags */}
      {topic.tags && topic.tags.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Теги:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {topic.tags.map((tag, index) => (
              <Chip 
                key={index} 
                label={tag} 
                size="small"
                sx={{ mt: 1 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Feedback Block */}
      <FeedbackBlock 
        onSubmit={(feedback) => {
          console.log('Feedback submitted:', feedback);
          // TODO: Implement feedback submission to API
        }}
      />
    </Container>
  );
};

export default TopicDetail; 